import { useEffect, useMemo, useState, useCallback } from "react";
import { Box, Tabs, Tab, CircularProgress } from "@mui/material";
import { Document, Page } from "react-pdf";
import { useParams, useLocation } from "react-router-dom";

import Details from "./components/Details";
import {
  useGetPatientRequestFormQuery,
  useGetRequestFormPdfQuery,
  api,
} from "../../services/api";
import { RootState, useAppDispatch, useAppSelector } from "../../store";
import { setupPdfWorker } from "./pdfWorkerSetup";

setupPdfWorker();

// Functional component representing the list of specimen details information
const SpecimenDetails = () => {
  const { id } = useParams();
  const location = useLocation();

  const { userDetails } = useAppSelector((state: RootState) => state.auth);

  const isDiscrepancyView =
    (location.state as { isDiscrepancyView?: boolean } | null)
      ?.isDiscrepancyView === true;
  const detailsTabValue = isDiscrepancyView
    ? "discrepancy_details"
    : "specimen_details";
  const detailsTabLabel = isDiscrepancyView
    ? "Discrepancy Details"
    : "Specimen Details";
  const [selectedTab, setSelectedTab] = useState(detailsTabValue);
  const [numPages, setNumPages] = useState<number>();

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const healthOrgId = userDetails?.health_org_id ?? 0;

  const patientId = id ? parseInt(id) : undefined;
  const {
    data: patientDetails,
    isLoading: isPatientDetailsLoading,
    isFetching,
  } = useGetPatientRequestFormQuery(
    { healthOrgId: healthOrgId as number, id: patientId as number },
    { skip: !healthOrgId || !patientId }
  );

  const requestFormId = patientDetails?.request_form?.id;
  const { data: requestFormPdfData, isLoading: isRequestFormLoading } =
    useGetRequestFormPdfQuery(
      {
        healthOrgId: healthOrgId as number,
        requestFormId: requestFormId as number,
      },
      { skip: !healthOrgId || !requestFormId }
    );

  const requestFormUrl =
    requestFormPdfData?.request_form?.request_form_pdf_presigned_url ||
    requestFormPdfData?.request_form?.request_form_pdf ||
    requestFormPdfData?.request_form?.doctor_sign_presigned_url;
  const qrCodeNumbers: string[] = useMemo(
    () =>
      Array.isArray(requestFormPdfData?.hazard_bag)
        ? (requestFormPdfData?.hazard_bag || [])
          .map((bag: HazardBag) => bag.qr_code_number)
          .filter(
            (v: unknown): v is string => typeof v === "string" && v.length > 0
          )
        : [],
    [requestFormPdfData?.hazard_bag]
  );
  const uniqueQrCodes = useMemo(
    () => Array.from(new Set(qrCodeNumbers)),
    [qrCodeNumbers]
  );
  const baseHazardBags: HazardBag[] = useMemo(
    () =>
      Array.isArray(requestFormPdfData?.hazard_bag)
        ? (requestFormPdfData?.hazard_bag as HazardBag[])
        : [],
    [requestFormPdfData?.hazard_bag]
  );
  const dispatch = useAppDispatch();
  const [hazardBagList, setHazardBagList] = useState<HazardBag[]>([]);
  const [isHazardBagLoading, setIsHazardBagLoading] = useState<boolean>(false);
  const [hazardCache, setHazardCache] = useState<Map<string, HazardBag>>(
    new Map()
  );
  const [hazardFailedCodes, setHazardFailedCodes] = useState<string[]>([]);

  // Merge fetched hazard bag details with base request form bag data, preferring server updates while preserving any base fields.
  const mergeHazardBags = useCallback(
    (
      codes: string[],
      cache: Map<string, HazardBag>,
      baseByQr: Map<string | undefined, HazardBag | undefined>
    ) =>
      codes
        .map((qr) => {
          const fetched = cache.get(qr);
          const base = baseByQr.get(qr);
          return fetched ? { ...(base ?? {}), ...fetched } : base;
        })
        .filter((bag): bag is HazardBag => !!bag),
    []
  );

  useEffect(() => {
    // Reset hazard cache when context changes to avoid leaking prior patient/org data.
    setHazardCache(new Map());
    setHazardFailedCodes([]);
    setHazardBagList([]);
  }, [healthOrgId, patientId]);

  useEffect(() => {
    let isCancelled = false;
    const subscriptions: Array<{ unsubscribe: () => void; abort: () => void }> =
      [];
    const fetchAll = async () => {
      if (!healthOrgId || uniqueQrCodes.length === 0) {
        setHazardBagList(baseHazardBags);
        setIsHazardBagLoading(false);
        return;
      }
      const baseByQr = new Map(
        baseHazardBags.map((bag) => [bag.qr_code_number, bag] as const)
      );
      const codesToFetch = uniqueQrCodes.filter(
        (qr) => !hazardCache.has(qr) && !hazardFailedCodes.includes(qr)
      );
      if (codesToFetch.length === 0) {
        const merged = mergeHazardBags(uniqueQrCodes, hazardCache, baseByQr);
        setHazardBagList(merged);
        setIsHazardBagLoading(false);
        return;
      }
      setIsHazardBagLoading(true);
      try {
        const results = await Promise.all(
          codesToFetch.map((qr) =>
            (() => {
              const subscription = dispatch(
                api.endpoints.getHazardBagByQr.initiate({
                  healthOrgId: healthOrgId as number,
                  qrCodeNumber: qr,
                })
              );
              subscriptions.push({
                unsubscribe: subscription.unsubscribe,
                abort: subscription.abort,
              });
              return subscription.unwrap().catch(() => undefined);
            })()
          )
        );
        if (!isCancelled) {
          const filtered: HazardBag[] = results.filter(
            (r): r is HazardBag => !!r
          );
          const updatedCache = new Map(hazardCache);
          const updatedFailedCodes = hazardFailedCodes.slice();
          let didCacheChange = false;
          let didFailedChange = false;
          filtered.forEach((bag) => {
            if (bag?.qr_code_number) {
              updatedCache.set(bag.qr_code_number, bag);
              didCacheChange = true;
            }
          });
          results.forEach((result, index) => {
            if (!result) {
              const code = codesToFetch[index];
              if (code && !updatedFailedCodes.includes(code)) {
                updatedFailedCodes.push(code);
                didFailedChange = true;
              }
            }
          });
          const merged = mergeHazardBags(uniqueQrCodes, updatedCache, baseByQr);
          if (didCacheChange) {
            setHazardCache(updatedCache);
          }
          if (didFailedChange) {
            setHazardFailedCodes(updatedFailedCodes);
          }
          setHazardBagList(merged.length > 0 ? merged : baseHazardBags);
        }
      } finally {
        if (!isCancelled) setIsHazardBagLoading(false);
      }
    };
    fetchAll();
    return () => {
      isCancelled = true;
      subscriptions.forEach((s) => {
        s.abort();
        s.unsubscribe();
      });
    };
  }, [
    healthOrgId,
    dispatch,
    uniqueQrCodes,
    baseHazardBags,
    hazardCache,
    hazardFailedCodes,
    mergeHazardBags,
  ]);

  return (
    <Box sx={{ marginTop: "5px" }}>
      <Tabs value={selectedTab} onChange={handleChange}>
        <Tab value="request_form" label="Request Form" />
        <Tab value={detailsTabValue} label={detailsTabLabel} />
      </Tabs>
      <Box
        style={{
          maxHeight: "calc(100vh - 195px)",
          overflow: "auto",
          marginTop: "10px",
        }}
        width={"100%"}
      >
        {selectedTab === "request_form" ? (
          isPatientDetailsLoading || isRequestFormLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "calc(100vh - 97px)",
              }}
            >
              <CircularProgress size={40} sx={{ marginLeft: "10px" }} />
            </Box>
          ) : (
            requestFormUrl && (
              <Document
                file={requestFormUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                // Log only the error name — the raw error can embed the signed
                // request-form URL (PHI), so it must not reach the console.
                onLoadError={(e) =>
                  console.error(
                    "PDF failed to load:",
                    e instanceof Error ? e.name : "unknown"
                  )
                }
                onSourceError={(e) =>
                  console.error(
                    "PDF source error:",
                    e instanceof Error ? e.name : "unknown"
                  )
                }
              >
                {Array.apply(null, Array(numPages))
                  .map((x, i) => i + 1)
                  .map((page) => (
                    <Page
                      key={page}
                      pageNumber={page}
                      scale={1.3}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  ))}
              </Document>
            )
          )
        ) : (
          <Details
            details={
              requestFormPdfData
                ? {
                  ...requestFormPdfData,
                  hazard_bag:
                    hazardBagList && hazardBagList.length > 0
                      ? hazardBagList
                      : baseHazardBags,
                }
                : hazardBagList.length > 0 || baseHazardBags.length > 0
                  ? {
                    hazard_bag:
                      hazardBagList && hazardBagList.length > 0
                        ? hazardBagList
                        : baseHazardBags,
                  }
                  : undefined
            }
            isLoading={
              isPatientDetailsLoading ||
              isRequestFormLoading ||
              isFetching ||
              isHazardBagLoading
            }
          />
        )}
      </Box>
    </Box>
  );
};
export default SpecimenDetails;
